import type {
  CheckoutAddressData,
  CheckoutFormState,
  CheckoutPayerData,
  CheckoutPersonalData,
  PaymentMethod,
} from './checkout.types';
import { createInitialCheckoutForm } from './checkout.constants';

const CHECKOUT_DRAFT_STORAGE_PREFIX = 'checkout:draft:';

export interface CheckoutDraftSnapshot {
  personal: CheckoutPersonalData;
  address: CheckoutAddressData;
  paymentMethod: PaymentMethod | null;
  card: {
    holderName: string;
    expiry: string;
  };
  payer: CheckoutPayerData;
  useStudentDataAsPayer: boolean;
  updateActionToken: string | null;
  updateStudentId: number | null;
  trackingRefToken: string | null;
}

const buildCheckoutDraftStorageKey = (
  planHash: string,
  trackingRefToken?: string | null,
): string => (
  `${CHECKOUT_DRAFT_STORAGE_PREFIX}${planHash.trim()}:${trackingRefToken?.trim() || 'direct'}`
);

export const buildCheckoutDraftSnapshot = (
  form: CheckoutFormState,
  options: {
    updateActionToken?: string | null;
    updateStudentId?: number | null;
    trackingRefToken?: string | null;
  } = {},
): CheckoutDraftSnapshot => ({
  personal: { ...form.personal },
  address: { ...form.address },
  paymentMethod: form.paymentMethod,
  card: {
    holderName: form.card.holderName,
    expiry: form.card.expiry,
  },
  payer: { ...form.payer },
  useStudentDataAsPayer: form.useStudentDataAsPayer,
  updateActionToken: options.updateActionToken?.trim() || null,
  updateStudentId: typeof options.updateStudentId === 'number' && options.updateStudentId > 0
    ? options.updateStudentId
    : null,
  trackingRefToken: options.trackingRefToken?.trim() || null,
});

export const saveCheckoutDraft = (
  planHash: string,
  draft: CheckoutDraftSnapshot,
  trackingRefToken?: string | null,
): void => {
  const normalizedHash = planHash.trim();
  if (!normalizedHash) {
    return;
  }

  try {
    sessionStorage.setItem(
      buildCheckoutDraftStorageKey(normalizedHash, trackingRefToken),
      JSON.stringify(draft),
    );
  } catch {
    // ignore storage failures
  }
};

export const loadCheckoutDraft = (
  planHash: string,
  trackingRefToken?: string | null,
): CheckoutDraftSnapshot | null => {
  const normalizedHash = planHash.trim();
  if (!normalizedHash) {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(
      buildCheckoutDraftStorageKey(normalizedHash, trackingRefToken),
    );
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<CheckoutDraftSnapshot>;
    if (!parsed || typeof parsed !== 'object' || !parsed.personal || !parsed.address) {
      return null;
    }

    return {
      personal: parsed.personal as CheckoutPersonalData,
      address: parsed.address as CheckoutAddressData,
      paymentMethod: parsed.paymentMethod ?? null,
      card: {
        holderName: typeof parsed.card?.holderName === 'string' ? parsed.card.holderName : '',
        expiry: typeof parsed.card?.expiry === 'string' ? parsed.card.expiry : '',
      },
      payer: {
        ...createInitialCheckoutForm().payer,
        ...(parsed.payer && typeof parsed.payer === 'object' ? parsed.payer as CheckoutPayerData : {}),
      },
      useStudentDataAsPayer: typeof parsed.useStudentDataAsPayer === 'boolean'
        ? parsed.useStudentDataAsPayer
        : false,
      updateActionToken: typeof parsed.updateActionToken === 'string'
        ? parsed.updateActionToken.trim() || null
        : null,
      updateStudentId: typeof parsed.updateStudentId === 'number' && parsed.updateStudentId > 0
        ? parsed.updateStudentId
        : null,
      trackingRefToken: typeof parsed.trackingRefToken === 'string'
        ? parsed.trackingRefToken.trim() || null
        : null,
    };
  } catch {
    return null;
  }
};

export const clearCheckoutDraft = (
  planHash: string,
  trackingRefToken?: string | null,
): void => {
  const normalizedHash = planHash.trim();
  if (!normalizedHash) {
    return;
  }

  try {
    sessionStorage.removeItem(buildCheckoutDraftStorageKey(normalizedHash, trackingRefToken));
  } catch {
    // ignore storage failures
  }
};

export const applyCheckoutDraftToForm = (
  draft: CheckoutDraftSnapshot,
  current: CheckoutFormState,
): CheckoutFormState => ({
  ...current,
  personal: { ...draft.personal },
  address: { ...draft.address },
  paymentMethod: draft.paymentMethod,
  card: {
    holderName: draft.card.holderName,
    number: '',
    expiry: draft.card.expiry,
    cvv: '',
  },
  payer: { ...draft.payer },
  useStudentDataAsPayer: draft.useStudentDataAsPayer,
});
